import * as React from "react";
import { Layout } from "../components/layout";
import { Cursor } from "../components/cursor";
import { SEO } from "../components/seo";
const EM_DASH = "–";
export var EntryMode;
(function (EntryMode) {
    EntryMode[EntryMode["Hiragana"] = 0] = "Hiragana";
    EntryMode[EntryMode["Katakana"] = 1] = "Katakana";
    EntryMode[EntryMode["Alpha"] = 2] = "Alpha";
})(EntryMode || (EntryMode = {}));
const ENTRY_MODE_EN = {
    [EntryMode.Hiragana]: "Hiragana",
    [EntryMode.Katakana]: "Katakana",
    [EntryMode.Alpha]: "Half-width Alphanumeric"
};
function createStartingState() {
    return {
        history: [
            {
                inputFocus: false,
                entryMode: EntryMode.Alpha,
                textContent: TextContent.Blank,
                lastKanaMode: EntryMode.Hiragana,
                converting: false
            }
        ],
        seen: {}
    };
}
export default class BasicsPage extends React.Component {
    constructor() {
        super(...arguments);
        this.state = createStartingState();
        this.previousStates = [];
    }
    getCurrentState() {
        return last(this.state.history);
    }
    render() {
        return (<Layout>
        <SEO title="Japanese IME Basics"/>
        <main>
          <article className="leading-normal">
            <header>
              <h1 className="text-center">Japanese IME Basics</h1>
            </header>
            <div className="sm:flex">
              <div className="sm:w-1/2">
                <aside>
                  <h2>State</h2>
                  <dl className="flex flex-wrap p-3">
                    {this.getStateDisplay()}
                  </dl>
                </aside>
                <section className="max-w-md">
                  <h2>Explanations</h2>
                  <ul>{this.getExplanations()}</ul>
                </section>
              </div>
              <div className="sm:py-6 sm:w-1/2">
                <div>{this.getTextBox()}</div>
                <div className="mt-4">{this.getActions()}</div>
              </div>
            </div>
          </article>
        </main>
      </Layout>);
    }
    getStateDisplay() {
        const currentState = this.getCurrentState();
        const ret = [];
        ret.push(<React.Fragment key="textBoxFocusState">
        <dt className="font-bold w-full lg:w-1/2">Text box focus state</dt>
        <dd className="lg:w-1/2">
          {currentState.inputFocus ? "Focused" : "Unfocused"}
        </dd>
      </React.Fragment>);
        const entryModeClass = `${currentState.inputFocus ? "" : "opacity-50"} ${this.state.seen.focused ? "" : "hidden"}`;
        ret.push(<React.Fragment key="entryModeState">
        <dt className={`font-bold w-full lg:w-1/2 ${entryModeClass}`}>
          Entry mode
        </dt>
        <dd className={`lg:w-1/2 ${entryModeClass}`}>
          {ENTRY_MODE_EN[currentState.entryMode]}
        </dd>
      </React.Fragment>);
        return ret;
    }
    getExplanations() {
        const ret = [];
        const currentState = this.getCurrentState();
        if (!this.state.seen.focused) {
            ret.push(<li key="textBoxExplanation" className="text-blue">
          The text box to the right is not real. While I'll speak like this was
          a real text box, it's not actually one. You can use the text box{" "}
          <em>below</em> to test your acquired knowledge.
        </li>);
        }
        if (currentState.textContent !== TextContent.Blank &&
            !currentState.inputFocus) {
            ret.push(<li key="committed">
          <strong className="text-red">Note:</strong> If you remove focus from
          the text box, you commit to the text currently displayed on the text
          box.
        </li>);
        }
        if (!currentState.inputFocus) {
            ret.push(<li key="inputFocusFalse">
          The IME will only be enabled when the focus is on anywhere you can
          type text in. Click on <em>Focus text box</em> to simulate focusing on
          the text box to the right.
        </li>);
        }
        if (currentState.inputFocus && !this.state.seen.anyEntry) {
            ret.push(<li key="anyEntrySeenFalse" className="text-blue">
          Great! Now explore each of the entry modes.
        </li>);
        }
        if (currentState.inputFocus &&
            currentState.textContent === TextContent.Blank) {
            ret.push(<li key="inputFocusTrueBlankText">
          There are three entry modes that we will focus on in this guide:
          Hiragana, Katakana, and Half-width Alphanumeric. Try to practice
          switching between the three in a real text box. Note that switching
          entry modes is only possible when a text box is in focus.
        </li>, <li key="ANoteOnToggles">
          This can be kind of confusing, but when switching <em>from</em>{" "}
          alphanumeric input, <kbd>Ctrl</kbd>+<kbd>Caps Lock</kbd> and{" "}
          <kbd>Alt</kbd>+<kbd>Caps Lock</kbd> will only <em>toggle</em> kana
          input regardless of the key combination.
        </li>);
        }
        if (currentState.textContent === TextContent.Abc) {
            ret.push(<li key="Abc">
          You just typed some text using the direct input (half-width
          alphanumeric) mode. This is useful for inputting text in English (or
          other languages), numbers, programming, and other text without
          switching your keyboard.
        </li>);
        }
        if (currentState.textContent === TextContent.Kitto &&
            currentState.converting) {
            ret.push(<li key="KittoConv">
          You just typed some text in hiragana. The IME will give you a list of
          predictions for this text, but for this specific tutorial, this is
          already the text that we want. Go ahead and commit to this text.
        </li>);
        }
        if (currentState.textContent === TextContent.Igirisu &&
            currentState.converting) {
            ret.push(<li key="IgirisuConv">
          You just typed some text in katakana. The IME will give you a list of
          predictions for this text, but for this specific tutorial, this is
          already the text that we want. Go ahead and commit to this text.
        </li>, <li key="ANoteOnKata">
          <strong>Note:</strong> Switching to katakana input is most often not
          required thanks to predictions.
        </li>);
        }
        if ((currentState.textContent === TextContent.Kitto ||
            currentState.textContent === TextContent.Igirisu) &&
            !currentState.converting) {
            ret.push(<li key="Kitto">
          Great. This text is now saved in the text box and the IME will now let
          you type more text.
        </li>);
        }
        if (currentState.textContent !== TextContent.Blank &&
            !currentState.converting) {
            ret.push(<li key="resetTextBoxTip">
          Clear the text box to see other tutorials.
        </li>);
        }
        return ret;
    }
    getTextBox() {
        const currentState = this.getCurrentState();
        return (<div className={`w-full sm:w-64 border-gray border-2 p-2 ${currentState.inputFocus
            ? "bg-transparent text-black"
            : "bg-inactive text-black-faded"}`}>
        {this.generateTextContent()}
      </div>);
    }
    getActions() {
        const actions = [];
        const currentState = this.getCurrentState();
        if (currentState.inputFocus) {
            actions.push(<button className="bg-red text-white hover:bg-red-lighter px-3 py-2 rounded" key="inputFocusTrue" onClick={() => this.setFocus(false)}>
          Unfocus text box
        </button>);
            if (currentState.textContent === TextContent.Blank) {
                actions.push(<button className="bg-blue text-white hover:bg-blue-lighter px-3 py-2 rounded" key="kata" onClick={() => this.setEntryMode(currentState.entryMode === EntryMode.Alpha
                    ? currentState.lastKanaMode
                    : EntryMode.Alpha)}>
            <kbd>Alt</kbd>+<kbd>`</kbd> (Toggle kana input)
          </button>);
                actions.push(<button className="bg-blue text-white hover:bg-blue-lighter px-3 py-2 rounded" key="hira" onClick={() => this.switchEntryMode("hira")}>
            <kbd>Ctrl</kbd>+<kbd>Caps Lock</kbd> (
            {currentState.entryMode === EntryMode.Alpha
                    ? "Toggle kana input"
                    : "Switch to Hiragana"}
            )
          </button>);
                actions.push(<button className="bg-blue text-white hover:bg-blue-lighter px-3 py-2 rounded" key="kata" onClick={() => this.switchEntryMode("kata")}>
            <kbd>Alt</kbd>+<kbd>Caps Lock</kbd> (
            {currentState.entryMode === EntryMode.Alpha
                    ? "Toggle kana input"
                    : "Switch to Katakana"}
            )
          </button>);
            }
            if (currentState.entryMode === EntryMode.Alpha &&
                currentState.textContent === TextContent.Blank) {
                actions.push(<button className="bg-blue text-white hover:bg-blue-lighter px-3 py-2 rounded" key="typeAbc" onClick={() => this.setTextContent(TextContent.Abc)}>
            <kbd>ABC</kbd> (Type "abc")
          </button>);
            }
            if (currentState.entryMode === EntryMode.Hiragana &&
                currentState.textContent === TextContent.Blank) {
                actions.push(<button className="bg-blue text-white hover:bg-blue-lighter px-3 py-2 rounded" key="typeKitto" onClick={() => {
                    this.setTextContent(TextContent.Kitto, true);
                }}>
            <kbd>KITTO</kbd> (Type "きっと" {EM_DASH} Hiragana tutorial)
          </button>);
            }
            if (currentState.entryMode === EntryMode.Katakana &&
                currentState.textContent === TextContent.Blank) {
                actions.push(<button className="bg-blue text-white hover:bg-blue-lighter px-3 py-2 rounded" key="typeIgirisu" onClick={() => {
                    this.setTextContent(TextContent.Igirisu, true);
                }}>
            <kbd>IGIRISU</kbd> (Type "イギリス" {EM_DASH} Katakana tutorial)
          </button>);
            }
            if (currentState.textContent !== TextContent.Blank &&
                !currentState.converting) {
                actions.push(<button className="bg-blue text-white hover:bg-blue-lighter px-3 py-2 rounded" key="clear" onClick={() => this.setTextContent(TextContent.Blank)}>
            <kbd>Ctrl</kbd>+<kbd>A</kbd>, <kbd>Bksp</kbd> (Clear the text box)
          </button>);
            }
            if ((currentState.textContent === TextContent.Kitto ||
                currentState.textContent === TextContent.Igirisu) &&
                currentState.converting) {
                actions.push(<button className="bg-blue text-white hover:bg-blue-lighter px-3 py-2 rounded" key="clear" onClick={() => this.setTextContent(currentState.textContent, false)}>
            <kbd>Enter</kbd> (Commit text)
          </button>);
            }
        }
        else {
            actions.push(<button className="bg-red text-white hover:bg-red-lighter px-3 py-2 rounded" key="inputFocusFalse" onClick={() => this.setFocus(true)}>
          Focus text box
        </button>);
        }
        actions.push(<button className="bg-primary text-white hover:bg-primary-lighter px-3 py-2 rounded" type="button" onClick={() => this.resetGuide()}>
        Reset this guide
      </button>);
        return (<nav className="mt-4 sm:mt-0 sm:ml-4 leading-none sm:w-auto">
        <div className="text-black-faded">Choose an action:</div>
        <ul className="list-reset">
          {actions.map(action => (<li className="p-2 border-gray">{action}</li>))}
        </ul>
      </nav>);
    }
    pushImeState(modifyPrevious) {
        this.setState(prevState => {
            const currentState = last(prevState.history);
            const newImeState = Object.assign({}, currentState, modifyPrevious(currentState));
            return {
                history: prevState.history.concat(newImeState)
            };
        });
    }
    see(obj) {
        this.setState(prev => ({ seen: { ...prev.seen, ...obj } }));
    }
    setFocus(inputFocus) {
        this.pushImeState(prev => ({
            inputFocus,
            converting: inputFocus ? prev.converting : false
        }));
        this.see({ focused: true });
    }
    resetGuide() {
        this.setState(createStartingState());
    }
    setTextContent(textContent, converting = false) {
        this.pushImeState(prev => ({ textContent, converting }));
        this.see({ anyEntry: true });
    }
    setEntryMode(entryMode) {
        this.pushImeState(prev => ({
            entryMode,
            lastKanaMode: entryMode === EntryMode.Hiragana || entryMode === EntryMode.Katakana
                ? entryMode
                : prev.lastKanaMode
        }));
    }
    switchEntryMode(key) {
        this.pushImeState(prev => {
            let newEntryMode;
            if (prev.entryMode === EntryMode.Alpha) {
                newEntryMode = prev.lastKanaMode;
            }
            else {
                newEntryMode = key === "hira" ? EntryMode.Hiragana : EntryMode.Katakana;
            }
            return {
                entryMode: newEntryMode,
                lastKanaMode: prev.entryMode === EntryMode.Hiragana ||
                    prev.entryMode === EntryMode.Katakana
                    ? newEntryMode
                    : prev.lastKanaMode
            };
        });
    }
    generateTextContent() {
        const currentState = this.getCurrentState();
        const textContent = currentState.textContent;
        const inputFocus = currentState.inputFocus;
        if (textContent === TextContent.Blank) {
            if (inputFocus)
                return <Cursor />;
            else
                return (<em className="text-black-faded">This is a simulated text box.</em>);
        }
        else if (textContent === TextContent.Abc) {
            if (inputFocus) {
                return (<span>
            abc
            <Cursor />
          </span>);
            }
            else {
                return "abc";
            }
        }
        else if (textContent === TextContent.Kitto) {
            if (inputFocus && currentState.converting) {
                return (<span className="border-b">
            きっと
            <Cursor />
          </span>);
            }
            else if (inputFocus) {
                return (<span>
            きっと
            <Cursor />
          </span>);
            }
            else {
                return "きっと";
            }
        }
        else if (textContent === TextContent.Igirisu) {
            if (inputFocus && currentState.converting) {
                return (<span className="border-b">
            イギリス
            <Cursor />
          </span>);
            }
            else if (inputFocus) {
                return (<span>
            イギリス
            <Cursor />
          </span>);
            }
            else {
                return "イギリス";
            }
        }
    }
}
export var TextContent;
(function (TextContent) {
    TextContent[TextContent["Blank"] = 0] = "Blank";
    TextContent[TextContent["Abc"] = 1] = "Abc";
    TextContent[TextContent["Kitto"] = 2] = "Kitto";
    TextContent[TextContent["Igirisu"] = 3] = "Igirisu";
})(TextContent || (TextContent = {}));
function last(arr) {
    return arr[arr.length - 1];
}
//# sourceMappingURL=basics.jsx.map